import { ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { first, lastValueFrom, Subscription } from 'rxjs';

import { WebcastModel } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel';
import { InsightSearchHelper } from 'rev-portal/search/InsightSearchHelper.Service';
import { IAccessEntity, IAccessEntitySearchHits } from 'rev-portal/search/Search.Service';
import { uniq } from 'rev-shared/util';
import { WebRtcListenerConnectionService } from 'rev-shared/webrtc/WebRtcListenerConnection.Service';

import { ProducerService } from '../Producer.Service';
import { IProducerOptions } from '../Contract';

import styles from '../ProducerWebcast.module.less';
import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';
import { TranslateService } from '@ngx-translate/core';

@Component({
	selector: 'backstage-view',
	templateUrl: './BackstageView.Component.html',
	host: { '[class]': 'styles.backstageRoot' },
	standalone: false
})
export class BackstageViewComponent implements OnInit, OnDestroy {
	@Input() public webcast: WebcastModel;
	@Input() public producerOptions: IProducerOptions;

	private readonly sub = new Subscription();
	public backstageAutoEnabled: boolean = true;
	public backstageCollapsed: boolean;
	public backstageVolume: number = 100;
	public hasLivePresenters: boolean;
	public initialized: boolean;
	public onstageVolume: number = 100;
	public userInPreview: boolean;
	public userIsLive: boolean;
	public userHasMic: boolean;
	public sceneCollapsed: boolean = true;
	public previousBackstageVolume: number;
	public previousOnstageVolume: number;

	public missingPresenters: IAccessEntity[] = [];
	public numMissingPresenters: number;
	public numActivePresenters: number;
	private stopPushToTalk: () => void;

	public readonly styles = styles;

	constructor(
		private readonly ngZone: NgZone,
		private readonly DialogService: DialogService,
		private readonly InsightSearchHelper: InsightSearchHelper,
		private readonly ListenerConnection: WebRtcListenerConnectionService,
		private readonly ProducerService: ProducerService,
		private readonly TranslateService: TranslateService
	) {}

	public ngOnChanges(): void {
		this.updatePresenterStatus();
	}

	public ngOnInit(): void {
		lastValueFrom(
			this.webcast.webcast$.pipe(
				first(w => !!w.webRtcListenerUrl),
			)
		)
			.then(() => this.ProducerService.start())
			.then(() => {
				this.initialized = true;
				this.initPresenters();
			})
			.catch(e => console.error('Error starting listener connection: ', e));
	}

	private initPresenters(): void {
		this.sub.add(
			this.ListenerConnection.presenters.presenters$.subscribe({
				next: () => this.ngZone.run(() => this.updatePresenterStatus()),
				error: e => console.error('Presenters error: ', e)
			}));
		this.sub.add(
			this.ProducerService.producerStreams$.subscribe(streams => {
				this.userHasMic = !!streams.micStream;
			}));
		this.sub.add(
			this.ListenerConnection.presenters.listenerMsg$.subscribe(msg => {
				if(msg === 'RecordingStopped' && this.webcast.currentUser.isAssignedEventAdmin) {
					this.DialogService.openAlertDialog(
						this.TranslateService.instant('Error'),
						this.TranslateService.instant('Event_Producer_RecordingStoppedError'),
						this.TranslateService.instant('Ok'));
				}
			}));
	}

	private updatePresenterStatus(): void {
		if(!this.ListenerConnection.presenters) {
			return;
		}

		this.updateMissingPresenters();

		const onstageUsers = this.ListenerConnection.presenters.getLayoutPresenters(this.producerOptions.onStageLayout)
			.map(p => p.id) ?? [];

		const currentUserId = this.webcast.currentUser.id;

		this.hasLivePresenters = onstageUsers.length > 0;
		this.userIsLive = onstageUsers.includes(currentUserId);
		this.userInPreview = !this.userIsLive &&
			this.ListenerConnection.presenters.getLayoutPresenters(this.producerOptions.backstageLayout).some(p => p.id === currentUserId);

		if (this.webcast.currentUser.isAssignedEventAdmin || !this.backstageAutoEnabled) {
			return;
		}

		this.backstageCollapsed = this.userInPreview || this.userIsLive;
	}

	public ngOnDestroy(): void {
		this.sub.unsubscribe();
		this.stopPushToTalk?.();
	}

	public toggleOnstageMute(): void {
		if (this.onstageVolume === 0) {
			this.onstageVolume = this.previousOnstageVolume || 0;
		} else {
			this.previousOnstageVolume = this.onstageVolume;
			this.onstageVolume = 0;
		}
	}

	public toggleBackstageMute(): void {
		if (this.backstageVolume === 0) {
			this.backstageVolume = this.previousBackstageVolume || 0;
		} else {
			this.previousBackstageVolume = this.backstageVolume;
			this.backstageVolume = 0;
		}
	}

	public toggleBackstagePanel(): void {
		this.backstageCollapsed = !this.backstageCollapsed;
		this.backstageAutoEnabled = false;
	}

	private updateMissingPresenters(): void {
		const activePresenters = this.ListenerConnection.presenters.presenters;
		const internalPresenterIds = uniq([...this.webcast.presenterIds, ...this.webcast.eventAdminIds]);
		const missingPresenterIds = internalPresenterIds.filter(id => !activePresenters.find(p => p.id === id));

		this.numActivePresenters = activePresenters.length;
		this.numMissingPresenters = internalPresenterIds.length + this.webcast.externalPresenters.length - this.numActivePresenters;

		if(this.webcast.currentUser.isExternalPresenter) {
			return;
		}

		this.loadMissingPresenters(missingPresenterIds)
			.then(accessEntities => {
				if(activePresenters !== this.ListenerConnection.presenters.presenters) {
					return;
				}

				this.missingPresenters = [
					...missingPresenterIds.map(id =>
						accessEntities.find(a => a.id === id) ||
						this.missingPresenters.find(p => p.id === id)
					),
					...this.webcast.externalPresenters.filter(ext =>
						!activePresenters.find(p => p.externalPresenterEmail === ext.email))
				];
			})
			.catch(e => console.log('BackstageView: ', e));
	}

	private loadMissingPresenters(missingPresenterIds: string[]): Promise<any[]> {
		const unloaded = missingPresenterIds.filter(id => !this.missingPresenters.find(p => p.id === id));
		if(!unloaded.length) {
			return Promise.resolve(unloaded);
		}
		return this.InsightSearchHelper.getAccessEntities(this.webcast.accountId, unloaded)
			.then((data: IAccessEntitySearchHits) => data.accessEntities);
	}


	public tryStartTalkStage(): void {
		if(this.stopPushToTalk){
			console.log('push to talk aleady active');
			return;
		}

		this.ProducerService.startPushToTalk()
			.then(stop => {
				this.stopPushToTalk = stop;
			})
			.catch(e => console.error('Push To Talk error: ', e));
	}

	public tryStopTalkStage(): void {
		this.stopPushToTalk?.();
		this.stopPushToTalk = null;
	}

}

